/**
 * CtaBackgroundControls — reusable subsection for the CTA tab. Lets an admin
 * upload a background image for a CTA card, position it (X% / Y%) and tune
 * the opacity so it blends with the section's gradient background.
 *
 * Used by Primary, Secondary and Final CTA sections — all three drive their
 * overlay from `{bgImageUrl, bgImagePosX, bgImagePosY, bgImageOpacity}` on
 * their content namespace.
 */
import { useRef, useState } from 'react';
import axios from 'axios';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Upload, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function CtaBackgroundControls({ value = {}, onChange, testIdPrefix }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const url = value.bgImageUrl || '';
  const posX = Number(value.bgImagePosX ?? 50);
  const posY = Number(value.bgImagePosY ?? 50);
  const opacity = Number(value.bgImageOpacity ?? 35);
  // When responsive is off, the image stays at its natural size and the admin
  // picks a horizontal alignment (left/center/right). Default: responsive on.
  const responsive = value.bgImageResponsive !== false;
  const align = ['left', 'center', 'right'].includes((value.bgImageAlign || '').toLowerCase())
    ? value.bgImageAlign
    : 'center';

  const patch = (partial) => onChange({ ...partial });

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('section', `cta_bg_${testIdPrefix}`);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/landing/upload-feature-image`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      patch({ bgImageUrl: res.data.image_url });
      toast.success('Background image uploaded');
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeImage = () => {
    patch({ bgImageUrl: '' });
  };

  return (
    <div className="pt-4 mt-4 border-t border-gray-200 space-y-3" data-testid={`${testIdPrefix}-bg-controls`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <Label className="text-sm font-semibold">Background image (optional)</Label>
          <p className="text-xs text-gray-500 mt-0.5">Upload an image that sits behind the CTA copy. Use opacity to blend it with the green gradient.</p>
        </div>
        {url ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={removeImage}
            data-testid={`${testIdPrefix}-bg-remove`}
          >
            <X className="w-3.5 h-3.5 mr-1" /> Remove
          </Button>
        ) : (
          <Button
            type="button"
            size="sm"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid={`${testIdPrefix}-bg-upload`}
          >
            <Upload className="w-3.5 h-3.5 mr-1" /> {uploading ? 'Uploading…' : 'Upload'}
          </Button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleUpload}
        />
      </div>

      {url && (
        <>
          {/* Live preview — shows the same gradient as the live section so the
              admin can judge the blend in-context without leaving the CMS. */}
          <div
            className="relative h-40 rounded-xl overflow-hidden border border-emerald-200"
            style={{ background: 'linear-gradient(135deg, #065f33 0%, #007a33 50%, #14a85a 100%)' }}
            data-testid={`${testIdPrefix}-bg-preview`}
          >
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `url(${url})`,
                backgroundSize: responsive ? 'cover' : 'auto',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: responsive ? `${posX}% ${posY}%` : `${align} ${posY}%`,
                opacity: opacity / 100,
                mixBlendMode: 'screen',
              }}
            />
            <div className="absolute inset-0 flex items-center justify-center text-white text-sm font-bold drop-shadow">
              Preview
            </div>
          </div>

          {/* Sizing mode toggle + alignment (only when non-responsive). */}
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={responsive}
                onChange={(e) => patch({ bgImageResponsive: e.target.checked })}
                className="mt-0.5 accent-emerald-600"
                data-testid={`${testIdPrefix}-bg-responsive-toggle`}
              />
              <span className="flex-1">
                <span className="block text-sm font-semibold text-gray-800">Fit image to container (responsive)</span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  When on, the image scales to cover the CTA card at all sizes. When off, the image stays at its natural size and you can align it left, centre, or right below.
                </span>
              </span>
            </label>

            {!responsive && (
              <div>
                <Label className="text-xs">Horizontal alignment</Label>
                <div className="flex gap-2 mt-1" data-testid={`${testIdPrefix}-bg-align-group`}>
                  {['left', 'center', 'right'].map((a) => (
                    <button
                      key={a}
                      type="button"
                      onClick={() => patch({ bgImageAlign: a })}
                      className={`flex-1 h-9 rounded-md border text-sm font-semibold capitalize transition-colors ${
                        align === a
                          ? 'bg-emerald-600 text-white border-emerald-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                      }`}
                      data-testid={`${testIdPrefix}-bg-align-${a}`}
                    >
                      {a}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-gray-400 mt-1">
                  Vertical position (below) still controls how the image sits vertically. The image will render at its true size, so a very large image may be cropped by the card edges.
                </p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {responsive ? (
              <SliderRow
                label="Horizontal position"
                hint="0% = left, 100% = right"
                value={posX}
                onChange={(v) => patch({ bgImagePosX: v })}
                min={0}
                max={100}
                unit="%"
                testId={`${testIdPrefix}-bg-pos-x`}
              />
            ) : (
              <div className="text-xs text-gray-400 italic pt-6">
                Horizontal position replaced by alignment buttons above.
              </div>
            )}
            <SliderRow
              label="Vertical position"
              hint="0% = top, 100% = bottom"
              value={posY}
              onChange={(v) => patch({ bgImagePosY: v })}
              min={0}
              max={100}
              unit="%"
              testId={`${testIdPrefix}-bg-pos-y`}
            />
            <SliderRow
              label="Opacity"
              hint="0% = invisible, 100% = full image"
              value={opacity}
              onChange={(v) => patch({ bgImageOpacity: v })}
              min={0}
              max={100}
              unit="%"
              testId={`${testIdPrefix}-bg-opacity`}
            />
          </div>
        </>
      )}
    </div>
  );
}

function SliderRow({ label, hint, value, onChange, min, max, unit, testId }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Label className="text-xs font-medium">{label}</Label>
        <span className="text-[11px] font-mono text-gray-600" data-testid={`${testId}-value`}>{value}{unit}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step="1"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-emerald-600"
        data-testid={testId}
      />
      <p className="text-[10px] text-gray-400 mt-0.5">{hint}</p>
    </div>
  );
}
